import { useState } from 'react';
import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'framer-motion';

import { RichHeading } from '@/components/common/Heading';
import { Icon } from '@/components/common/Icon';
import { HeadingLevel } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { PortableText } from '@/lib/sanity';

import type { SanityFaqType } from '@/types/sanity';

export interface FaqProps extends SanityFaqType {}

const Faq = ({ heading, faqItems }: FaqProps) => {
  return (
    <Section verticalPadding backgroundColour="--background-subtle">
      <FaqWrapper>
        <HeadingLevel>
          <HeadingWrapper>
            <RichHeading heading={heading} size="headline-lg" />
          </HeadingWrapper>
          <ul>
            {faqItems.map((faqItem) => (
              <FaqItem key={faqItem._key} {...faqItem} />
            ))}
          </ul>
        </HeadingLevel>
      </FaqWrapper>
    </Section>
  );
};

interface FaqItemProps {
  title?: string;
  description?: any;
  _key: string;
  trackingTag?: { blockName: string; blockAction: string };
}

const FaqItem = ({ title, description, _key, trackingTag }: FaqItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <ContentWrapper>
      <FaqItemWrapper>
        <FaqItemButton
          type="button"
          aria-label={title}
          aria-expanded={isOpen}
          aria-controls={`item${_key}`}
          id={`faq${_key}id`}
          className={
            trackingTag?.blockName &&
            `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
          }
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <Question>{title}</Question>
          <ChevronWrapper isOpen={isOpen}>
            <Icon
              icon="ChevronDown"
              colour={isOpen ? '--icon-action' : '--icon-inverse-strong'}
              size="15px"
            />
          </ChevronWrapper>
        </FaqItemButton>
      </FaqItemWrapper>
      <AnimatePresence initial={false}>
        {isOpen && (
          <FaqSection
            id={`item${_key}`}
            aria-labelledby={`faq${_key}id`}
            key="content"
            initial="closed"
            animate={isOpen ? 'open' : 'closed'}
            exit="closed"
            variants={{
              open: { opacity: 1, height: 'auto' },
              closed: { opacity: 0, height: 0 },
            }}
            transition={{
              duration: 0.5,
              ease: [0.04, 0.62, 0.23, 0.98],
            }}
          >
            <PortableText value={description} />
          </FaqSection>
        )}
      </AnimatePresence>
    </ContentWrapper>
  );
};

const FaqWrapper = styled.div`
  padding: var(--spacing-800) var(--spacing-250) var(--spacing-500);
  background-color: var(--surface-primary-subtle);
  border-radius: var(--radius-s);
`;

const FaqItemButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: center;
  width: 100%;
  padding: 0;
  margin: 0;
  background-color: transparent;
  border: none;
  text-align: left;
  color: var(--text-strong);
  font-size: var(--font-size-small);
  cursor: pointer;
`;

const FaqSection = styled(motion.div)`
  overflow: hidden;
  p {
    padding: var(--spacing-medium);
    font-size: var(--font-size-small);
  }
`;

const Question = styled.span`
  padding: var(--spacing-small) var(--spacing-xx-small);
  display: flex;
  flex-wrap: wrap;
  font-size: var(--font-size-medium);
`;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-xxxx-large);
  text-align: center;
`;

const ChevronWrapper = styled.div<{ isOpen: boolean }>`
  border: 1.5px solid var(--icon-action);
  border-radius: 50%;
  padding: var(--spacing-150);
  transition: background-color 0.6s;
  background-color: ${({ isOpen }) =>
    isOpen ? 'transparent' : 'var(--icon-action)'};

  svg {
    transition: transform 0.6s;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'none')};
  }
`;

const FaqItemWrapper = styled.h3`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.li`
  &:not(:last-child) {
    border-bottom: 1px solid rgba(var(--colour-deep-space-rgb), 0.1);
  }
`;

export { Faq };
